// CoachReferencesCard — main column above-the-fold references list.
//
// Replaces the inline ReferencesCard that lived in PlayerProfile.tsx
// (PR1 left it intact; PR2 supersedes). Section-level "Platform
// verified" attribution pill is conditional: it only renders when at
// least one reference in the list has verified_by === 'platform'.
// Showing it unconditionally would mislead — a player with all-
// unverified references shouldn't carry a platform-verified seal at the
// section level.
//
// Each reference is a bordered subcard with avatar (initials), coach
// name + meta line, star row from star_rating (DB column 1..5; null
// renders an empty star row, NOT a 5-star default — the prior brief
// hint was overridden by the schema reality), and an italic quote
// with a left rule.

import { CircleCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { ReferenceVerifiedBy } from '../../types'
import Avatar from '../Avatar'
import {
  CoachContactLine,
  type CoachContactValues,
} from './CoachContact'

interface Reference {
  id: string
  coach_id: string | null
  coach_name: string
  // Migration 0017 — public coach-profile slug when the authoring coach
  // has a row; the name links to /coaches/:slug. Null for legacy rows.
  coach_slug: string | null
  // Migration 0018 — server-shaped, mutually exclusive: real values only
  // when the viewer is a Pro coach; a locked flag otherwise; neither when
  // the coach has no contact set.
  coach_contact?: CoachContactValues
  contact_locked?: boolean
  team: string | null
  season: string | null
  star_rating: number | null
  quote: string | null
  verified_by: ReferenceVerifiedBy
  created_at: string
}

interface Props {
  references: Reference[]
  playerSlug: string
  canWriteReference: boolean
  // Migration 0018 — a coach seeing a locked reference is a FREE coach;
  // the teaser label links to /pricing for them (upsell moment).
  viewerIsCoach: boolean
}

export default function CoachReferencesCard({
  references,
  playerSlug,
  canWriteReference,
  viewerIsCoach,
}: Props) {
  const hasPlatformVerified = references.some((r) => r.verified_by === 'platform')

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-5">
      <header className="flex items-center justify-between mb-3 gap-3">
        <h2 className="text-[12px] font-bold uppercase tracking-wider text-hoop-accent">
          Coach References{references.length > 0 && (
            <span className="ml-1 text-hoop-text-3 font-normal normal-case tracking-normal">
              · {references.length}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-3">
          {hasPlatformVerified && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-semibold bg-hoop-accent-light text-hoop-accent">
              <CircleCheck className="w-3 h-3" />
              Platform verified
            </span>
          )}
          {canWriteReference && (
            <Link
              to={`/players/${playerSlug}/reference/new`}
              className="text-xs font-medium text-hoop-text hover:text-hoop-text-2"
            >
              + Write reference
            </Link>
          )}
        </div>
      </header>

      {references.length === 0 ? (
        <p className="text-sm text-hoop-text-3">
          No references yet.
        </p>
      ) : (
        <ul className="space-y-3">
          {references.map((ref) => (
            <ReferenceItem
              key={ref.id}
              reference={ref}
              viewerIsCoach={viewerIsCoach}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function ReferenceItem({
  reference: ref,
  viewerIsCoach,
}: {
  reference: Reference
  viewerIsCoach: boolean
}) {
  const meta = [ref.team, ref.season].filter(Boolean).join(' · ')

  return (
    <li className="border border-hoop-border rounded-[10px] p-4">
      <div className="flex items-start gap-3">
        <Avatar
          src={null}
          name={ref.coach_name}
          className="w-[34px] h-[34px] rounded-full flex-shrink-0"
          textClassName="text-xs"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="font-semibold text-sm text-hoop-text">
                {/* Migration 0017 — coach name links to the public coach
                    profile when one exists. Minimal <a>, no redesign. */}
                {ref.coach_slug ? (
                  <Link
                    to={`/coaches/${ref.coach_slug}`}
                    className="hover:underline"
                  >
                    {ref.coach_name}
                  </Link>
                ) : (
                  ref.coach_name
                )}
              </div>
              {meta && (
                <div className="text-xs text-hoop-text-3">
                  {meta}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <StarRow rating={ref.star_rating} />
              {ref.verified_by === 'platform' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-hoop-accent-light text-hoop-accent">
                  <CircleCheck className="w-2.5 h-2.5" />
                  Platform verified
                </span>
              )}
            </div>
          </div>

          {ref.quote && (
            <blockquote
              className="mt-2 italic text-hoop-text-2 border-l-[3px] border-hoop-border pl-3 whitespace-pre-line"
              style={{ fontSize: '13px' }}
            >
              {ref.quote}
            </blockquote>
          )}

          {/* Migration 0018 — per-reference contact: values / blurred
              teaser / nothing, decided by which keys the server shipped. */}
          <CoachContactLine
            contact={ref.coach_contact}
            contactLocked={ref.contact_locked}
            viewerIsCoach={viewerIsCoach}
          />
        </div>
      </div>
    </li>
  )
}

function StarRow({ rating }: { rating: number | null }) {
  // Render five star glyphs always; filled glyphs reflect the rating.
  // Per schema CHECK, rating is 1..5 or null; clamp defensively.
  const filled = rating == null ? 0 : Math.max(0, Math.min(5, rating))
  return (
    <span
      className="inline-flex items-center leading-none"
      style={{ fontSize: '13px', letterSpacing: '1px' }}
      aria-label={rating ? `${rating} of 5 stars` : 'unrated'}
    >
      <span className="text-hoop-star">{'★'.repeat(filled)}</span>
      <span className="text-hoop-border">
        {'★'.repeat(5 - filled)}
      </span>
    </span>
  )
}
